/**
 * Cart Billing Information Handler
 * Handles the billing type selection and form validation for invoice fields
 */

// Load BillingStorage helper
// Note: billing-storage.js should be loaded before this file
class CartBillingInfo {
  constructor() {
    this.init();
  }

  /**
   * Valida y formatea un RUT chileno.
   * Retorna true si el RUT es válido, de lo contrario, false.
   * @param {string} rut - El RUT a validar, con o sin formato.
   */
  validateRut(rut) {
    // Limpiar el RUT de puntos, guiones y espacios
    let cleanedRut = rut.replace(/\./g, "").replace(/-/g, "").replace(/\s/g, "").toUpperCase();

    // Verificar el formato básico del RUT
    if (cleanedRut.length < 2) {
      return false;
    }

    // Separar el cuerpo del RUT del dígito verificador
    let body = cleanedRut.slice(0, -1);
    let dv = cleanedRut.slice(-1);

    // Verificar que el cuerpo contenga solo números y no esté vacío
    if (!/^[0-9]+$/.test(body)) {
      return false;
    }

    // Calcular el dígito verificador (Algoritmo Módulo 11)
    let sum = 0;
    let factor = 2;
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body.charAt(i), 10) * factor;
      factor = factor === 7 ? 2 : factor + 1;
    }

    let calculatedDv = 11 - (sum % 11);
    if (calculatedDv === 11) {
      calculatedDv = "0";
    } else if (calculatedDv === 10) {
      calculatedDv = "K";
    } else {
      calculatedDv = String(calculatedDv);
    }

    // Comparar el dígito verificador calculado con el original
    return String(calculatedDv) === dv;
  }

  /**
   * Formatea un RUT con puntos y guión.
   * @param {string} rut - El RUT a formatear.
   */
  formatRut(rut) {
    let cleanedRut = rut.replace(/\./g, "").replace(/-/g, "").replace(/\s/g, "").toUpperCase();
    
    if (cleanedRut.length < 2) {
      return rut;
    }
    
    let body = cleanedRut.slice(0, -1);
    let dv = cleanedRut.slice(-1);

    // Agregar puntos cada 3 dígitos de atrás hacia adelante
    let formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    return `${formattedBody}-${dv}`;
  }

  init() {
    this.bindEvents();
    this.setupInitialState();
  }

  bindEvents() {
    // Handle billing type radio button changes
    document.addEventListener('change', (event) => {
      if (event.target.name === 'attributes[factura_tipo]') {
        this.handleBillingTypeChange(event.target.value);
      }
    });
  }

  validateField(field) {
    const errorElement = document.getElementById(`${field.id}-error`);
    let isValid = true;
    let errorMessage = '';

    // Clear previous error
    this.clearFieldError(field);

    // Check if field is required and empty
    if (field.hasAttribute('required') && !field.value.trim()) {
      isValid = false;
      errorMessage = 'Este campo es obligatorio';
    }

    // Email validation
    if (field.type === 'email' && field.value.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(field.value)) {
        isValid = false;
        errorMessage = 'Por favor, introduce un email válido';
      }
    }

    // Phone validation
    if (field.type === 'tel' && field.value.trim()) {
      const phoneRegex = /^[\+]?[0-9\s\-\(\)]{9,}$/;
      if (!phoneRegex.test(field.value)) {
        isValid = false;
        errorMessage = 'Por favor, introduce un número de teléfono válido';
      }
    }

    // RUT validation for Chilean tax ID
    if (field.name === 'attributes[factura_rut]' && field.value.trim()) {
      if (!this.validateRut(field.value.trim())) {
        isValid = false;
        errorMessage = 'Por favor, introduce un RUT válido (ej: 12.345.678-9)';
      }
    }

    // Show error if invalid
    if (!isValid && errorElement) {
      errorElement.textContent = errorMessage;
      errorElement.classList.add('show');
      field.classList.add('error');
    }

    return isValid;
  }

  clearFieldError(field) {
    const errorElement = document.getElementById(`${field.id}-error`);
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.classList.remove('show');
    }
    field.classList.remove('error');
  }

  handleRutInput(field) {
    const cursorPosition = field.selectionStart;
    const value = field.value;
    
    // Only format if the field has content and it looks like it could be a RUT
    if (value.length > 0) {
      // Remove any existing formatting
      const cleanValue = value.replace(/\./g, "").replace(/-/g, "").replace(/\s/g, "");
      
      // Only proceed if we have at least 2 characters (minimum for a RUT)
      if (cleanValue.length >= 2) {
        const formattedValue = this.formatRut(cleanValue);
        
        // Only update if the formatted value is different
        if (formattedValue !== value) {
          field.value = formattedValue;
          
          // Try to maintain cursor position
          const newCursorPos = Math.min(cursorPosition + (formattedValue.length - value.length), formattedValue.length);
          field.setSelectionRange(newCursorPos, newCursorPos);
        }
      }
    }
  }

  validateForm(event) {
    const billingType = document.querySelector('input[name="attributes[factura_tipo]"]:checked');
    
    if (billingType && billingType.value === 'factura') {
      // Check both cart drawer and main cart page
      const invoiceFields = document.getElementById('invoice-fields');
      const cartInvoiceFields = document.getElementById('cart-invoice-fields');
      
      const fieldsToValidate = [];
      if (invoiceFields) fieldsToValidate.push(invoiceFields);
      if (cartInvoiceFields) fieldsToValidate.push(cartInvoiceFields);
      
      let isValid = true;

      fieldsToValidate.forEach(fields => {
        const requiredFields = fields.querySelectorAll('input[required], textarea[required]');
        requiredFields.forEach(field => {
          if (!this.validateField(field)) {
            isValid = false;
          }
        });
      });

      if (!isValid) {
        event.preventDefault();
        // Scroll to first error field
        const firstError = document.querySelector('.field-error.show');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
      }
    }

    return true;
  }

  persistField(field) {
    if (!window.BillingStorage) return;
    
    const key = field.name.replace('attributes[', '').replace(']', '');
    window.BillingStorage.save({ [key]: field.value.trim() });
  }

  prefillFromStorage() {
    // This method is not needed since billing fields only exist in the modal
    // The modal's prefillFromStorage() method handles autofill when the modal opens
  }

  filterAttributesForSubmission(form) {
    const billingType = form.querySelector('input[name="attributes[factura_tipo]"]:checked');
    
    // If boleta is selected, temporarily disable invoice fields so they don't get submitted
    if (billingType && billingType.value === 'boleta') {
      // Get all invoice fields except the billing type field
      const allInvoiceFields = form.querySelectorAll('input[name^="attributes[factura_"], textarea[name^="attributes[factura_"]');
      const invoiceFields = Array.from(allInvoiceFields).filter(field => field.name !== 'attributes[factura_tipo]');
      
      // Store original disabled states
      const originalStates = [];
      invoiceFields.forEach((field, index) => {
        originalStates[index] = field.disabled;
        field.disabled = true; // Temporarily disable so they won't be submitted
      });
      
      // Re-enable after a short delay (after form submission)
      setTimeout(() => {
        invoiceFields.forEach((field, index) => {
          field.disabled = originalStates[index];
        });
      }, 100);
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new CartBillingInfo();
});

// Also initialize when cart drawer content is updated
document.addEventListener('cart-drawer:updated', () => {
  new CartBillingInfo();
}); 